import { useMemo } from 'react';
import { SingleUnit } from './equipmentTypes';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useUserContext } from '../../contexts/UserContext';
import { UnitRecord } from './equipmentTypes';
import { buildUnitOfMeasureOptions } from '../../utils/unitOfMeasureUtils';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { buildEquipmentCatalogAutosuggestOptions } from './equipmentAutosuggestOptions';

interface UseEquipmentCategoryAutosuggestOptions {
    categoryId?: string;
    formData: Partial<SingleUnit>;
    unit?: SingleUnit | null;
}

export const useEquipmentCategoryAutosuggest = ({
    categoryId,
    formData,
    unit,
}: UseEquipmentCategoryAutosuggestOptions) => {
    const { companyUnits } = useEquipmentContext();
    const { company, companyOwner } = useUserContext();
    const companyId = (company?.id as string) || companyOwner?.companyId || '';

    const formRecord = formData as Record<string, unknown>;
    const unitRecord = unit as UnitRecord | null | undefined;

    const catalogOptionsByField = useMemo(
        () =>
            buildEquipmentCatalogAutosuggestOptions({
                companyUnits,
                categoryId,
                manufacturerName:
                    (formRecord.manufacturerName as string | undefined) || unitRecord?.manufacturerName || '',
                engineManufacturer:
                    (formRecord.engineManufacturer as string | undefined) ||
                    unitRecord?.engineManufacturer ||
                    '',
                engineModelNumber:
                    (formRecord.engineModelNumber as string | undefined) || unitRecord?.engineModelNumber || '',
            }),
        [categoryId, companyUnits, formData, unit],
    );

    const unitOfMeasureExtraValues = useMemo(
        () =>
            companyUnits
                .map((listedUnit) => listedUnit.unitOfMeasure)
                .filter((value): value is string => Boolean(value?.trim())),
        [companyUnits],
    );

    const { catalogOptions } = useCompanyUnitsOfMeasure(companyId, unitOfMeasureExtraValues);

    const unitOfMeasureOptions = useMemo(
        () => buildUnitOfMeasureOptions(unitOfMeasureExtraValues, catalogOptions),
        [unitOfMeasureExtraValues, catalogOptions],
    );

    return {
        ...catalogOptionsByField,
        unitOfMeasure: unitOfMeasureOptions,
        unitOfMeasureExtraValues,
    };
};
